import React, { FC, PointerEvent, useMemo, useRef, useState } from 'react';
import { AiPrimaryButton } from 'src/components/widgets/ai';
import { GhostButton } from 'src/components/widgets/buttons';
import theme from 'src/shared/theme';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { AgentDraft, AgentDraftEditResponse, AgentGesture, AgentTurn, PreviewDraftEdit, ProductSide, UpsertAgentPrintAreaInput } from './types';
import { gestureAreaEdit } from './gestureAreaEdit';

type Point = { x: number; y: number };

const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 12px;
    padding: 12px;
    border: 1px solid ${theme.ai.calloutBorder};
    border-radius: 12px;
    background: #faf5ff;
`;

const Hint = styled.p`
    margin: 0;
    color: #455a64;
    font-size: 12px;
    line-height: 1.45;
`;

const Stage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    padding: 16px;
    overflow: hidden;
    border-radius: 9px;
    background: #dfe5e8;
`;

const ImageFrame = styled.div<{ $aspectRatio: number }>`
    position: relative;
    width: min(100%, ${(props) => 420 * props.$aspectRatio}px);
    max-height: 420px;
    aspect-ratio: ${(props) => props.$aspectRatio};
    overflow: hidden;
    touch-action: none;
    user-select: none;

    > img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        pointer-events: none;
    }
`;

const Rectangle = styled.div<{ $from: Point; $to: Point }>`
    position: absolute;
    left: ${(props) => Math.min(props.$from.x, props.$to.x) * 100}%;
    top: ${(props) => Math.min(props.$from.y, props.$to.y) * 100}%;
    width: ${(props) => Math.abs(props.$to.x - props.$from.x) * 100}%;
    height: ${(props) => Math.abs(props.$to.y - props.$from.y) * 100}%;
    border: 2px solid ${theme.ai.gradientMid};
    background: rgba(168, 85, 247, 0.13);
    pointer-events: none;
`;

const Line = styled.div<{ $from: Point; $to: Point }>`
    position: absolute;
    left: ${(props) => props.$from.x * 100}%;
    top: ${(props) => props.$from.y * 100}%;
    width: ${(props) => Math.hypot(props.$to.x - props.$from.x, props.$to.y - props.$from.y) * 100}%;
    height: 3px;
    transform: rotate(${(props) => Math.atan2(props.$to.y - props.$from.y, props.$to.x - props.$from.x)}rad);
    transform-origin: left center;
    background: ${theme.ai.gradientMid};
    box-shadow: 0 0 0 1px white;
    pointer-events: none;
`;

const LengthInput = styled.input`
    width: 140px;
    height: 36px;
    padding: 0 10px;
    border: 1px solid #b0bec5;
    border-radius: 7px;
    background: white;
    color: #263238;
`;

const Footer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
`;

const Missing = styled.div`
    padding: 16px;
    border-radius: 8px;
    background: #fff3e0;
    color: #e65100;
    font-size: 12px;
`;

// A clear way out of drawing: describe the change to the assistant instead. Sits under the editor, on its own row.
const DescribeRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 4px;
    border-top: 1px dashed ${theme.ai.calloutBorder};
    color: #607d8b;
    font-size: 12px;
`;

const DescribeButton = styled.button`
    padding: 0;
    border: 0;
    background: none;
    color: ${theme.ai.solid};
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: ${theme.ai.gradientMid};
    }
`;

const findSide = (draft: AgentDraft, sideRef: string) => {
    for (const variant of draft.document?.product?.variants ?? []) {
        for (const side of variant.sides ?? []) {
            if (side.entityReferenceKey === sideRef) return side;
        }
    }
    return null;
};

const normalizedStartingRectangle = (draft: AgentDraft, side: ProductSide, sideRef: string) => {
    const width = side.image?.widthPx ?? 0;
    const height = side.image?.heightPx ?? 0;
    if (width <= 0 || height <= 0) return null;

    const suggestion = draft.document?._meta?.suggestions?.find((entry) => entry.subject === sideRef)?.value;
    if (suggestion?.widthPx && suggestion.heightPx) {
        return {
            from: { x: (suggestion.xPx ?? 0) / width, y: (suggestion.yPx ?? 0) / height },
            to: { x: ((suggestion.xPx ?? 0) + suggestion.widthPx) / width, y: ((suggestion.yPx ?? 0) + suggestion.heightPx) / height }
        };
    }

    const raw = side.areas?.[0]?.bounds?.raw;
    if (!raw?.width || !raw.height) return null;
    return {
        from: { x: (raw.x + width / 2) / width, y: (raw.y + height / 2) / height },
        to: { x: (raw.x + width / 2 + raw.width) / width, y: (raw.y + height / 2 + raw.height) / height }
    };
};

interface AgentGestureCardProps {
    request: AgentGesture;
    draft: AgentDraft;
    defaultMessages?: AgentTurn['defaultMessages'];
    disabled?: boolean;
    onSubmit: (message: string, gesture?: AgentGesture | null) => void;
    onConfirmArea: (input: PreviewDraftEdit<UpsertAgentPrintAreaInput>) => Promise<AgentDraftEditResponse | null>;
    /** Focuses the composer so the merchant can describe the change to the assistant instead of drawing it. */
    onDescribe?: () => void;
}

export const AgentGestureCard: FC<AgentGestureCardProps> = ({ request, draft, defaultMessages, disabled, onSubmit, onConfirmArea, onDescribe }) => {
    const stageRef = useRef<HTMLDivElement>(null);
    const side = findSide(draft, request.sideRef);
    const startingRectangle = useMemo(() => (side ? normalizedStartingRectangle(draft, side, request.sideRef) : null), [draft, request.sideRef, side]);
    const startingLine = useMemo(() => {
        if (request.kind !== 'calibrate' || !side?.image?.widthPx) return null;
        const referenceWidth = draft.document?._meta?.calibration?.[request.sideRef]?.referenceWidthPx ?? 0;
        if (referenceWidth <= 0) return null;
        const segment = Math.min(referenceWidth / side.image.widthPx, 1);
        const centerX = startingRectangle ? (startingRectangle.from.x + startingRectangle.to.x) / 2 : 0.5;
        const centerY = startingRectangle ? (startingRectangle.from.y + startingRectangle.to.y) / 2 : 0.5;
        return {
            from: { x: Math.max(centerX - segment / 2, 0), y: centerY },
            to: { x: Math.min(centerX + segment / 2, 1), y: centerY }
        };
    }, [draft.document?._meta?.calibration, request.kind, request.sideRef, side?.image?.widthPx, startingRectangle]);
    const [from, setFrom] = useState<Point | null>(() => (request.kind === 'area' ? startingRectangle?.from : startingLine?.from) ?? null);
    const [to, setTo] = useState<Point | null>(() => (request.kind === 'area' ? startingRectangle?.to : startingLine?.to) ?? null);
    const [hasChanged, setHasChanged] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [lengthCm, setLengthCm] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [confirmError, setConfirmError] = useState(false);

    const image = side?.image;
    const kind = request.kind;
    const needsLength = kind === 'measure' || kind === 'calibrate';

    const eventPoint = (event: PointerEvent<HTMLDivElement>): Point => {
        const box = stageRef.current?.getBoundingClientRect();
        if (!box) return { x: 0, y: 0 };
        return {
            x: Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1),
            y: Math.min(Math.max((event.clientY - box.top) / box.height, 0), 1)
        };
    };

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (disabled || confirming || kind === 'calibrate') return;
        event.currentTarget.setPointerCapture(event.pointerId);
        const point = eventPoint(event);
        setFrom(point);
        setTo(point);
        setHasChanged(true);
        setIsDrawing(true);
    };

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (!isDrawing || !from) return;
        let point = eventPoint(event);
        if (kind === 'measure') {
            point = Math.abs(point.x - from.x) >= Math.abs(point.y - from.y) ? { x: point.x, y: from.y } : { x: from.x, y: point.y };
        }
        setTo(point);
    };

    const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
        if (!isDrawing) return;
        event.currentTarget.releasePointerCapture(event.pointerId);
        setIsDrawing(false);
    };

    const hasDrawing = from && to && (Math.abs(to.x - from.x) > 0.01 || Math.abs(to.y - from.y) > 0.01);
    const canSubmit = !disabled && !confirming && (kind === 'calibrate' || hasDrawing) && (!needsLength || Number(lengthCm) > 0);

    const submit = async () => {
        if (!canSubmit || !side) return;
        const sideName = side.name || request.sideRef;

        if (kind === 'calibrate') {
            onSubmit(T._('The reference line on {side} is {length} cm long.', 'Admin').replace('{side}', sideName).replace('{length}', lengthCm), null);
            return;
        }

        if (kind === 'area') {
            const input = from && to && gestureAreaEdit(draft, request.sideRef, from, to);
            if (!input) {
                setConfirmError(true);
                return;
            }
            setConfirming(true);
            setConfirmError(false);
            try {
                const result = await onConfirmArea(input);
                if (!result) {
                    setConfirmError(true);
                    return;
                }
                const message = hasChanged
                    ? defaultMessages?.sendArea ?? T._('I updated the print area on {side} in the draft.', 'Admin')
                    : defaultMessages?.confirmArea ?? T._('The proposed print area for {side} is fine.', 'Admin');
                onSubmit(message.replace('{side}', sideName), null);
            } finally {
                setConfirming(false);
            }
            return;
        }

        if (!from || !to || !image?.widthPx || !image.heightPx) return;
        const gesture: AgentGesture = {
            kind,
            sideRef: request.sideRef,
            x1: from.x * image.widthPx,
            y1: from.y * image.heightPx,
            x2: to.x * image.widthPx,
            y2: to.y * image.heightPx
        };
        const text = T._('The line I drew on {side} is {length} cm long.', 'Admin').replace('{side}', sideName).replace('{length}', lengthCm);
        onSubmit(text, gesture);
    };

    if (!side || !image?.url) return <Missing>{T._('The image requested by the assistant is not available in the current draft.', 'Admin')}</Missing>;
    if (!image.widthPx || !image.heightPx) return <Missing>{T._('The image dimensions are missing, so a precise gesture cannot be sent.', 'Admin')}</Missing>;

    return (
        <Card>
            <Hint>
                {kind === 'area'
                    ? startingRectangle && !hasChanged
                        ? T._('Review the proposed area. Confirm it, or draw a new rectangle directly on the image.', 'Admin')
                        : T._('Press and drag on the image to draw the print area.', 'Admin')
                    : kind === 'calibrate'
                      ? T._('Measure the indicated reference on the real product and enter its length.', 'Admin')
                      : T._('Press and drag along a measurable edge, then enter its real length.', 'Admin')}
            </Hint>
            <Stage>
                <ImageFrame
                    ref={stageRef}
                    $aspectRatio={image.widthPx / image.heightPx}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                >
                    <img src={image.url} alt={side.name || T._('Product side', 'Admin')} draggable={false} />
                    {from && to && (kind === 'area' ? <Rectangle $from={from} $to={to} /> : <Line $from={from} $to={to} />)}
                </ImageFrame>
            </Stage>
            {confirmError && <Missing role='alert'>{T._('The area could not be saved. Refresh the draft and try again.', 'Admin')}</Missing>}
            <Footer>
                {needsLength && (
                    <LengthInput
                        type='number'
                        min='0.1'
                        step='0.1'
                        value={lengthCm}
                        onChange={(event) => setLengthCm(event.target.value)}
                        placeholder={T._('Length in cm', 'Admin')}
                        aria-label={T._('Length in centimetres', 'Admin')}
                    />
                )}
                <AiPrimaryButton type='button' $size='small' disabled={!canSubmit} onClick={submit}>
                    {kind === 'area'
                        ? startingRectangle && !hasChanged
                            ? T._('Confirm area', 'Admin')
                            : T._('Send area', 'Admin')
                        : T._('Send measurement', 'Admin')}
                </AiPrimaryButton>
                {kind === 'area' && startingRectangle && !hasChanged && (
                    <GhostButton
                        type='button'
                        $size='small'
                        disabled={confirming}
                        onClick={() => {
                            setFrom(null);
                            setTo(null);
                            setHasChanged(true);
                        }}
                    >
                        {T._('Adjust', 'Admin')}
                    </GhostButton>
                )}
            </Footer>
            {onDescribe && (
                <DescribeRow>
                    {T._("Don't want to draw?", 'Admin')}
                    <DescribeButton type='button' onClick={onDescribe}>
                        {T._('Describe the change to the assistant', 'Admin')}
                    </DescribeButton>
                </DescribeRow>
            )}
        </Card>
    );
};
